import React, { useEffect, useRef, useState, FormEvent, ChangeEvent } from "react"
import classnames from "classnames"
import parse, { attributesToProps, domToReact, DOMNode, HTMLReactParserOptions } from "html-react-parser"

import ErrorIcon from "images/icons/icon-error-triangle.svg?raw"

import styles from "./activation.module.scss"

import {
  trackServiceConnectionStarted,
  trackServiceConnectionFailed,
} from "app/utils/mixpanel_tracking"

interface FormFieldOption {
  label: string
  value: string
}

interface FormField {
  name: string
  prompt: string
  field_type: string
  required: boolean
  protected: boolean
  default_value?: string
  helper_text?: string
  options?: FormFieldOption[]
}

interface Props {
  service: {
    name: string
    module_name: string
  }
  formFields: FormField[]
  formDescription?: string
  error?: string
  liveChannelId?: string
  submitUrl: string
}

const helperTextParseOptions: HTMLReactParserOptions = {
  replace: (node) => {
    if (node.type === "tag" && "name" in node && node.name === "a") {
      return (
        <a {...attributesToProps(node.attribs)} target="_blank" rel="noopener noreferrer">
          {domToReact(node.children as DOMNode[], helperTextParseOptions)}
        </a>
      )
    }
  },
}

const ActivateServiceWithModularAuth = ({ service, formFields, formDescription, error, liveChannelId, submitUrl }: Props) => {
  const [formValues, setFormValues] = useState<{ [key: string]: string }>(() => {
    const values: { [key: string]: string } = {}
    formFields.forEach(field => {
      values[field.name] = field.default_value || ""
    })
    return values
  })

  const handleInputChange = (fieldName: string, value: string) => {
    setFormValues(prev => ({
      ...prev,
      [fieldName]: value
    }))
  }

  const isNewConnection = !liveChannelId
  const connectionContext = "manual"
  const entrypoint = "service_settings"
  const authType = "modular"

  const hasTrackedStartedRef = useRef(false)
  const hasTrackedFailureRef = useRef(false)

  useEffect(() => {
    if (error && !hasTrackedFailureRef.current) {
      hasTrackedFailureRef.current = true

      trackServiceConnectionFailed({
        serviceName: service.name,
        serviceModuleName: service.module_name,
        authType,
        connectionContext,
        isNewConnection,
        entrypoint,
        errorType: error,
      })
    }
  }, [error, isNewConnection, service.name, service.module_name])

  const getFieldType = (fieldType: string, isProtected: boolean): string => {
    if (isProtected) {
      return "password"
    }

    switch (fieldType) {
      case "PASSWORD":
        return "password"
      case "URL":
        return "url"
      case "TEXT":
      default:
        return "text"
    }
  }

  const isFormValid = formFields.every(field => !field.required || formValues[field.name])

  return (
    <div className={styles.activation}>
      <div className={styles["service-fields"]}>
        <h1>Connect to {service.name}</h1>
        <div className={styles["service-activation-form"]}>
          <form
            className="new_live_channel"
            id="new_live_channel"
            method="post"
            action={submitUrl}
            onSubmit={(e: FormEvent<HTMLFormElement>) => {
              if (hasTrackedStartedRef.current) return

              e.preventDefault()
              hasTrackedStartedRef.current = true

              trackServiceConnectionStarted({
                serviceName: service.name,
                serviceModuleName: service.module_name,
                authType,
                connectionContext,
                isNewConnection,
                entrypoint,
              })

              // Mixpanel tracking is async; prevent the navigation from cancelling the request by
              // submitting on the next tick (or after a flush, if supported).
              const form = e.currentTarget
              const mixpanelAny = (window as any)?.mixpanel

              if (typeof mixpanelAny?.flush === "function") {
                Promise.resolve(mixpanelAny.flush())
                  .catch(() => null)
                  .finally(() => form.submit())
              } else {
                window.setTimeout(() => form.submit(), 250)
              }
            }}
          >
            <input type="hidden" name="authenticity_token" value={document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || ""} />
            {liveChannelId && <input type="hidden" name="live_channel_id" value={liveChannelId} />}

            <div className={styles["live-channel-fields"]}>
              {formDescription && (
                <p className={styles["form-description"]}>{parse(formDescription)}</p>
              )}
              {formFields.map((field, index) => (
                <div
                  key={field.name}
                  className={classnames(
                    styles["activation-field-entry"],
                    { [styles.error]: !!error }
                  )}
                >
                  <label htmlFor={field.name}>
                    {field.prompt}
                    {field.required && " *"}
                  </label>
                  {field.field_type === "SELECT" ? (
                    <select
                      autoFocus={index === 0}
                      name={`user_input[${field.name}]`}
                      id={field.name}
                      value={formValues[field.name] || ""}
                      onChange={(e: ChangeEvent<HTMLSelectElement>) => handleInputChange(field.name, e.target.value)}
                      required={field.required}
                    >
                      {!field.default_value && <option value="">Select...</option>}
                      {(field.options || []).map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type={getFieldType(field.field_type, field.protected)}
                      autoFocus={index === 0}
                      name={`user_input[${field.name}]`}
                      id={field.name}
                      value={formValues[field.name] || ""}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => handleInputChange(field.name, e.target.value)}
                      required={field.required}
                      autoComplete={field.protected ? "off" : "on"}
                    />
                  )}
                  {field.helper_text && (
                    <p className={styles["helper-text"]}>{parse(field.helper_text, helperTextParseOptions)}</p>
                  )}
                </div>
              ))}

              {error && (
                <div className={classnames(styles["after-message"], styles.submit)}>
                  <span>{parse(ErrorIcon)}</span>
                  {error}
                </div>
              )}

              <div className={styles["send-pin"]}>
                <button
                  className="button-secondary"
                  type="submit"
                  disabled={!isFormValid}
                >
                  Connect
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default ActivateServiceWithModularAuth
